import type { BlockStateUnionType } from '@unifyapps/defs/types/block';
import { parseRuntimeBlockId } from './parseRuntimeBlockId';

type BlocksState = Record<string, BlockStateUnionType | undefined>;
type GetBlockConfig = (originalBlockId: string) => BlockStateUnionType | undefined;

/**
 * Resolves a block state for any block ID — including untouched repeatable instances.
 *
 * Resolution order:
 *   1. If the ID is already materialized in the store, return that.
 *   2. If the ID is a runtime instance ID, synthesize a state object from the parent's
 *      original block config and the instance's runtime ID. This is the lazy "default"
 *      state — what an instance looks like before any user interaction has materialized it.
 *   3. Otherwise undefined (block doesn't exist anywhere).
 *
 * The resolver is a pure function — no React, no hooks, no side effects. Callers
 * are responsible for passing in the current `blocksState` slice and a config lookup.
 */
export function resolveBlockState(
  blockId: string,
  blocksState: BlocksState,
  getBlockConfig: GetBlockConfig,
): BlockStateUnionType | undefined {
  const existing = blocksState[blockId];
  if (existing) {
    return existing;
  }

  const parsed = parseRuntimeBlockId(blockId);
  if (!parsed.isInstance) {
    return undefined;
  }

  const parentConfig = getBlockConfig(parsed.originalBlockId);
  if (!parentConfig) {
    return undefined;
  }

  // pure read path — never mutate the returned object
  const synthesized = { ...parentConfig, id: blockId } as BlockStateUnionType;
  if (process.env.NODE_ENV !== 'production') {
    const contentValue = (synthesized as { content?: unknown }).content;
    if (contentValue && typeof contentValue === 'object') {
      Object.freeze(contentValue);
    }
    Object.freeze(synthesized);
  }
  return synthesized;
}
